import React, { type FC, useEffect } from 'react'
import { Box, Divider, type PopoverProps, svgIconClasses, type SxProps, type Theme, Typography } from '@mui/material'

import { assert } from '../../../utils'

import {
    Capability,
    ConnectwareError,
    ConnectwareErrorType,
    createIsAuthenticatedWithCapabilitiesSelector,
    type CybusServiceDeviation,
    selectServiceDeviationPage,
    Translation,
} from '../../../domain'

import { FormattedTranslation, useTranslator } from '../Internationalization'
import { AbsoluteRouteOnlyPath, AbsoluteRoutePathWithServiceId, PermissionedLink, usePermissionedLink, useRouting } from '../routing'
import { useAppState } from '../State'
import { SIDE_BAR_ICON_SIZE } from '../theme'
import { AnchorProvider, useAnchorCloser, useAnchorSetter } from '../common/Anchor'
import { AlertTriangleOutlineIcon } from '../common/Branding'
import { IconButton } from '../common/Button'
import Popover from '../common/Popover'

export const selectCanReadDeviations = createIsAuthenticatedWithCapabilitiesSelector(Capability.DEVIATIONS_READ)

/** Always `warning` - deviations have no other severity. The darker amber keeps the white number readable. */
const countBadgeStyle: SxProps<Theme> = {
    alignItems: 'center',
    bgcolor: 'warning.700',
    borderRadius: '50%',
    color: 'common.white',
    display: 'flex',
    fontSize: 8,
    fontWeight: 500,
    height: 12,
    justifyContent: 'center',
    lineHeight: 1,
    position: 'absolute',
    right: -1, // offsets the trigger IconButton's own 1px transparent border, whose padding box (not border box) is this badge's containing block
    top: -1,
    width: 12,
}

/**
 * The icon is the side bar's icon size, matching the side bar's open/close toggle below it - not a leftover default.
 * The hover is the side bar's `grey.200`, because the ghost button's own hover grey is the side bar's background.
 * Sized via this nested selector, not directly on the icon: `MuiIconButton`'s theme override forces
 * default-size icons to 16x16 via the same selector shape, and MUI always appends `sx` after theme
 * `styleOverrides` for a given component, so matching that shape here reliably wins.
 */
const triggerStyle: SxProps<Theme> = {
    height: 32,
    position: 'relative',
    width: 32,
    [`& .${svgIconClasses.root}`]: { height: SIDE_BAR_ICON_SIZE, width: SIDE_BAR_ICON_SIZE },
    '&:hover': { backgroundColor: 'grey.200' },
    '&:active': { backgroundColor: 'grey.300' },
}

/** Fixed card width, not a spacing-scale value. */
const POPOVER_WIDTH = 300

/**
 * No arrow - the gap of the `side` placement, between the trigger and the popover to its right, bottom-aligned with it:
 * 6px past the side bar's edge, as the trigger sits 8px inside it.
 */
const POPOVER_GAP = 14

/** `side` next to the side navigation, `below` under a trigger in the top bar */
type PopoverPlacement = 'side' | 'below'

const popoverOrigins: Readonly<Record<PopoverPlacement, Pick<PopoverProps, 'anchorOrigin' | 'transformOrigin'>>> = {
    side: { anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'bottom', horizontal: -POPOVER_GAP } },
    below: { anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } },
}

type DeviationsBadgeProps = Readonly<{ popoverPlacement?: PopoverPlacement }>

const popoverPaperStyle: SxProps<Theme> = { bgcolor: 'common.white', border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }

const popoverContentStyle: SxProps<Theme> = { width: POPOVER_WIDTH }

/** gap: 2.5 = 10px, a fractional spacing step - same idiom as `ToastZone.tsx`'s `ZONE_GAP`. */
const headerStyle: SxProps<Theme> = { alignItems: 'center', display: 'flex', gap: 2.5, px: 3, py: 2 }

/** Soft badge style - distinct from the trigger's solid `countBadgeStyle` above. */
const headerCountBadgeStyle: SxProps<Theme> = {
    alignItems: 'center',
    bgcolor: 'warning.50',
    borderRadius: '50%',
    color: 'warning.600',
    display: 'flex',
    flexShrink: 0,
    height: 36,
    justifyContent: 'center',
    width: 36,
}

/** Distinct from `grey.300` below (row separators) - two different border shades, deliberately not one reused value. */
const dividerStyle: SxProps<Theme> = { borderColor: 'grey.200' }

/** Bottom border between rows only, not after the last - the "+N more"/footer sections have their own, independent border logic below. */
const rowStyle: SxProps<Theme> = {
    color: 'grey.700',
    px: 3,
    py: 1,
    '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: 'grey.300' },
}

/** `grey.400` matches the rest of this popover's muted text - not a separate shade. */
const moreRowStyle: SxProps<Theme> = { borderBottom: '1px solid', borderColor: 'grey.300', color: 'grey.400', px: 3, py: 1 }

const footerStyle: SxProps<Theme> = { alignItems: 'center', borderTop: '1px solid', borderColor: 'grey.200', display: 'flex', px: 3, py: 2 }
const footerLinkStyle: SxProps<Theme> = { color: 'primary.700' }

const RECENT_DEVIATIONS_LIMIT = 5

const DeviationRow: FC<Readonly<{ id: CybusServiceDeviation['id'] }>> = ({ id }) => {
    const PermissionedServiceLink = usePermissionedLink()

    return (
        <Box data-testid="deviation-popover-row" sx={rowStyle}>
            <PermissionedServiceLink path={AbsoluteRoutePathWithServiceId.SERVICES_OVERVIEW_SERVICE} id={id} sx={{ color: 'inherit' }}>
                <Typography variant="caption" color="inherit">
                    {id}
                </Typography>
            </PermissionedServiceLink>
        </Box>
    )
}

const DeviationsPopoverContent: FC = () => {
    const page = useAppState(selectServiceDeviationPage)

    /** Only ever rendered by `DeviationsBadgeContent` once it has already confirmed `count > 0`, which requires a loaded, non-null page. */
    assert(page !== null, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Deviations popover rendered without a loaded page'))

    const count = page.totalCount
    const recent = page.current.slice(0, RECENT_DEVIATIONS_LIMIT)
    const moreCount = count - recent.length

    return (
        <Box data-testid="deviation-popover" sx={popoverContentStyle}>
            <Box sx={headerStyle}>
                <Box sx={headerCountBadgeStyle}>
                    <Typography variant="body2" color="inherit">
                        {count}
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'grey.900' }}>
                    <FormattedTranslation id={Translation.DEVIATION_DETECTED} values={{ count }} />
                </Typography>
            </Box>

            <Divider sx={dividerStyle} />

            <Box>
                {recent.map(({ id }) => <DeviationRow key={id} id={id} />)}
            </Box>

            {moreCount > 0 && (
                <Box data-testid="deviation-popover-more" sx={moreRowStyle}>
                    <Typography variant="caption" color="inherit">
                        <FormattedTranslation id={Translation.DEVIATION_POPOVER_MORE} values={{ count: moreCount }} />
                    </Typography>
                </Box>
            )}

            <Box sx={footerStyle}>
                <PermissionedLink data-testid="deviation-popover-view-all" path={AbsoluteRouteOnlyPath.DEVIATED_SERVICES} sx={footerLinkStyle}>
                    <Typography variant="caption" color="inherit">
                        <FormattedTranslation id={Translation.DEVIATION_VIEW_ALL} />
                    </Typography>
                </PermissionedLink>
            </Box>
        </Box>
    )
}

const DeviationsBadgeContent: FC<Required<DeviationsBadgeProps>> = ({ popoverPlacement }) => {
    const translator = useTranslator()
    const setAnchor = useAnchorSetter()
    const close = useAnchorCloser()
    const currentPath = useRouting().getCurrentPath()

    /**
     * The badge lives in the frame and stays mounted across page changes, so the popover is closed whenever the page
     * changes - by one of its own links, or any other way - instead of staying open over the new page. The full path,
     * ids included, so moving from one service's page to another's (what the popover's rows do) closes it too.
     */
    useEffect(() => close(), [currentPath])
    const page = useAppState(selectServiceDeviationPage)
    const count = page?.totalCount ?? 0

    if (count === 0) {
        return null
    }

    return (
        <>
            <IconButton
                data-testid="deviation-badge"
                aria-label={translator.formatTranslation(Translation.DEVIATION_TOAST_COUNTER, { count })}
                variant="ghost"
                sx={triggerStyle}
                onClick={(e) => setAnchor(e.currentTarget)}
            >
                <AlertTriangleOutlineIcon />
                <Box data-testid="deviation-badge-count" sx={countBadgeStyle}>
                    {count}
                </Box>
            </IconButton>
            <Popover
                onClose={close}
                {...popoverOrigins[popoverPlacement]}
                elevation={3} // lighter shadow than the default (8), which maps to this theme's heaviest token
                slotProps={{ paper: { sx: popoverPaperStyle } }}
            >
                <DeviationsPopoverContent />
            </Popover>
        </>
    )
}

/**
 * Icon + amber count badge + click-to-open popover, mounted via the `deviationsBadge` slots of `SideNavigation` and the
 * mobile top bar (`Navigation/` and `DeviationNotifications/` are separate feature folders).
 * Renders nothing at all when there are no active deviations, not just a bare icon.
 */
export const DeviationsBadge: FC<DeviationsBadgeProps> = ({ popoverPlacement = 'side' }) => {
    const canReadDeviations = useAppState(selectCanReadDeviations)

    if (!canReadDeviations) {
        return null
    }

    return (
        <AnchorProvider>
            <DeviationsBadgeContent popoverPlacement={popoverPlacement} />
        </AnchorProvider>
    )
}
